import React, { useEffect, useRef, useState, useCallback, useMemo, type JSX } from 'react';
import type { Call } from '@twilio/voice-sdk';

import { useSessionStore } from '@features/auth';
import { useTwilioVoice } from '@hooks/useTwilioVoice';
import { useTranslation } from 'react-i18next';
import { useTwilioStore } from '@store/twilio/useTwilioStore';
import { type UpdateNotesPayload, useAgentApi } from '@features/agent/hooks/useAgentApi';
import type { UiContact, CleanCallSession } from '@features/agent/types/agentDialer';

import PreSessionScreen from '@features/agent/components/PreSessionScreen';
import PausedSessionScreen from '@features/agent/components/PausedSessionScreen';
import DialerLayout from '@ui/shared/Dialer/DialerLayout';
import ContactCard from '@ui/shared/Dialer/ContactCard';
import Dialpad from '@ui/shared/Dialer/Dialpad';
import IntentCard from '@ui/shared/Dialer/IntentCard';
import NotesCard from '@ui/shared/Dialer/NotesCard';
import DispositionsModal from '@ui/shared/Dialer/DispositionsModal';

import { Card, CardContent } from '@core-ui/Components/ui/card';
import { Building2, Key, User, ClipboardList, LocateFixed } from 'lucide-react';

import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';
import { logError } from '@lib/logger/logError';

import DialerBodyGate from '@features/agent/components/ContactLoadingCard.tsx';
import clsx from 'clsx';
import { edgeApi } from '@lib/apiClients.ts';
import { microServicesRoutes } from '@constants';
import { type AxiosResponse } from 'axios';
import { type OrganizationInfo } from '@features/dashboard/types/dashboard.types.ts';
import useToastMessage from '@lib/logger/toast.ts';

const POLL_INTERVAL_IDLE_MS = 10_000;
const POLL_INTERVAL_ACTIVE_MS = 3_000;
const SPEECH_REFRESH_MS = 3_000;

// How long a session may look like it holds a transfer nobody picked up before
// the lock is released. The agent flags the session a beat before the SDR's
// screen answers, so a brief mismatch is normal and must not release anything;
// a real transfer keeps the SDR busy for as long as the call lasts, and never
// produces this combination at all.
const ORPHAN_TRANSFER_GRACE_MS = 30_000;

const DISPOSITION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'LVM', label: 'Left Voicemail' },
  { value: 'NA', label: 'No Answer' },
  { value: 'GC', label: 'Gatekeeper Conversation' },
  { value: 'PTNC', label: 'Phone Tree No Connection' },
  { value: 'BN/BS', label: 'Bad Number/Busy Signal' },
  { value: 'WN', label: 'Wrong Number' },
  { value: 'RAC', label: 'Referred to another contact' },
  { value: 'NLC', label: 'No Longer With Company' },
  { value: 'PR', label: 'Phone Refusal (hangup)' },
  { value: 'RTT', label: 'Refuse to transfer' },
  { value: 'OO', label: 'Opt Out' },
  { value: 'SA', label: 'Set Appointment' },
  { value: 'DMC', label: 'Decision Maker Conversation' },
  // {value: 'AII', label: 'AI Interaction'}
];

interface MinimalUser {
  id?: string;
  _id?: string;
  userId?: string;
  user?: { id?: string } | null;
  sdrId?: string | number | null;
  organizationId?: string | number | null;
  sdrName?: string | null;
  organizationName: string;
}

interface PartialValidations {
  validationsPassed?: boolean;
  voiceNote?: { data?: { url?: string | null } | null };
  sdr?: { data?: { id?: null | string, sdrId?: string | null; sdrName?: string | null; organizationId?: string | null; organizationName?: string | null } | null };
  callSession?: { isValid?: boolean; data?: { id?: string; status?: string | null } | null };
}

// Snapshot of the call/contact the disposition modal was opened for. It is
// captured the moment the call ends and is the only thing the submit handler
// reads, so a next call connecting (incoming legs are auto-accepted) or a
// contact rotation while the modal is still open cannot redirect the writes.
interface DispositionTarget {
  callId: string | null;
  contactId: string | null;
  contactName: string | null;
  callSessionId: string | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const getErrorMessage = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === 'string' ? e : 'Unexpected error';

const hasMute = (c: unknown): c is Call & { mute: (b: boolean) => void } =>
  isRecord(c) && typeof (c as { mute?: unknown }).mute === 'function';

const isRec = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const getStrSafe = (o: Record<string, unknown>, k: string): string | null => {
  const v = o[k];
  return typeof v === 'string' ? v : null;
};

interface HasParameters { parameters?: unknown }
interface HasCustomParameters { customParameters?: unknown }

const extractCallSid = (c: unknown): string | null => {
  let sid1: string | null = null;
  if (isRec(c) && isRec((c as HasParameters).parameters )) {
    const params = (c as HasParameters).parameters as Record<string, unknown>;
    sid1 = getStrSafe(params, 'CallSid');
  }

  // customParameters.get('CallSid') (Voice SDK)
  let sid2: string | null = null;
  if (isRec(c) && isRec((c as HasCustomParameters).customParameters )) {
    const cp = (c as HasCustomParameters).customParameters as Record<string, unknown>;
    const getFn = typeof cp.get === 'function' ? (cp.get as (k: string) => unknown) : null;
    const maybe = (getFn != null) ? getFn('CallSid') : null;
    sid2 = typeof maybe === 'string' ? maybe : null;
  }

  return sid1 ?? sid2 ?? null;
};

const asPartialValidations = (v: unknown): PartialValidations => (isRecord(v) ? (v as PartialValidations) : {});

export default function AgentsPage (): JSX.Element {
  const { showToast } = useToastMessage();
  const currentUser = useSessionStore((s) => s.currentUser);
  const [userRaw, setUserRaw] = useState<MinimalUser | null>(null);
  const twilioStatus = useTwilioStore((s) => s.status);
  const hasActiveCall = useTwilioStore((s) => s.hasActiveCall);
  const callSid = useTwilioStore((s) => s.callSid);
  const activeCall = useTwilioStore((s) => s.activeCall);
  const setIsMuted = useTwilioStore((s) => s.setIsMuted);

  const [callSession, setCallSessionRaw] = useState<CleanCallSession | null>(null);
  const intentBody = callSession?.intentBody ?? null;
  const [currentContact, setCurrentContact] = useState<UiContact | null>(null);
  const [queueLeft, setQueueLeft] = useState<number>(0);

  const [isOnTransfer, setIsOnTransfer] = useState<boolean>(false);
  const [currentSdrId, setCurrentSdrId] = useState<string>('');

  const setCallSession = useCallback( (session: Partial<CleanCallSession> | null) => {
    if (session == null) {
      setCallSessionRaw(null);
      return;
    }
    setCallSessionRaw((prev) => {
      const clean: CleanCallSession = {
        id: session.id ?? prev?.id ?? '',

        status: session.status ?? prev?.status,
        scriptId: session.scriptId ?? prev?.scriptId,
        scriptTitle: session.scriptTitle ?? prev?.scriptTitle,

        sdrId: session.sdrId ?? prev?.sdrId ?? null,
        sdrName: session.sdrName ?? prev?.sdrName ?? null,
        sdrStatus: session.sdrStatus ?? prev?.sdrStatus ?? null,
        organizationId: session.organizationId ?? prev?.organizationId ?? null,
        organizationName: session.organizationName ?? prev?.organizationName ?? null,

        agents: session.agents ?? prev?.agents ?? null,
        voiceDropsIds: session.voiceDropsIds ?? prev?.voiceDropsIds ?? null,
        contactList: session.contactList ?? prev?.contactList ?? null,
        notes: session.notes ?? prev?.notes ?? null,
        action: session.action ?? prev?.action ?? null,
        voiceNoteId: session.voiceNoteId ?? prev?.voiceNoteId ?? null,

        intentBody: session.intentBody ?? prev?.intentBody ?? null,
        voicemailUrl: session.voicemailUrl ?? prev?.voicemailUrl ?? null,
      };
      return clean;
    });
  }, []);

  const getOrganizationName = async (): Promise<string> => {
    const url = `${microServicesRoutes.edge.get.organizations.getById}/${String(currentUser?.organizationId)}`;
    try {
      const org: AxiosResponse<OrganizationInfo> = await edgeApi.get(url);
      const name = org.data.name ?? '';
      return name;
    } catch (error) {
      await logError(
        `Could not retrieve organization with id: ${currentUser?.organizationId}`, getOrganizationName.name,
        { error }, false
      );
      return '';
    }
  };

  useEffect(() => {
    const fetchOrgData: () => Promise<void> = async () => {
      const organizationName = await getOrganizationName();

      setUserRaw({
        id: currentUser?.userId,
        sdrId: currentUser?.sdrId,
        organizationId: currentUser?.organizationId,
        sdrName: currentUser?.sdrName,
        organizationName, 
        userId: currentUser?.userId,
      });
    };

    void fetchOrgData();
  }, []);

  const agentId: string | null = useMemo(() => {
    return userRaw?.userId?.toString?.() ??
      userRaw?.id?.toString?.() ??
      userRaw?._id?.toString?.() ??
      userRaw?.userId?.toString?.() ??
      null;
  }, [userRaw]);

  const { startCall, endCall, sendDigits, currentCall } = useTwilioVoice(agentId ?? undefined);

  const mergeCallSession = useCallback((patch: Partial<CleanCallSession>) => {
    setCallSessionRaw((prev) => {
      if (prev == null) {
        const seed: CleanCallSession = {
          id: patch.id ?? '',
          ...patch,
        };
        return seed;
      }
      return { ...prev, ...patch };
    });
  }, []);

  const [callSessionId, setCallSessionId] = useState<string | null>(callSession?.id ?? null);
  const [muted, setMutedLocal] = useState(false);
  const [started, setStarted] = useState(false);
  const [sessionPaused, setSessionPaused] = useState(false);
  const [showDisposition, setShowDisposition] = useState(false);
  const [dispositionValue, setDispositionValue] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [showSpecify, setShowSpecify] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [dispositionTarget, setDispositionTarget] = useState<DispositionTarget | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  
  const tickRunningRef = useRef(false);
  const startedRef = useRef(false);
  const callTransferredByMeRef = useRef(false);
  const postTransferHoldRef = useRef(false);
  const transferLocalWindowRef = useRef<number>(0);
  const needsNextContactOnResumeRef = useRef(false);
  const callIdRef = useRef('');
  const orphanTransferSinceRef = useRef<number | null>(null);
  const [transferLockReleased, setTransferLockReleased] = useState(false);

  const isWithinLocalTransferWindow = (ms: number = 4000): boolean => {
    const last = transferLocalWindowRef.current ?? 0;
    const diff = Date.now() - last;
    return diff < ms;
  };

  const lastCallIdRef = useRef<string | null>(null);

  type Rec = Record<string, unknown>;
  const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
  const getStr = (r: Rec, k: string): string | undefined => (typeof r[k] === 'string' ? (r[k] as string) : undefined);
  const getArrStr = (r: Rec, k: string): string[] | null =>
    Array.isArray(r[k]) && (r[k] as unknown[]).every((x) => typeof x === 'string') ? (r[k] as string[]) : null;

  const getSpeechFromRaw = (raw: Rec): string => {
    const data = isRec(raw.data) ? raw.data : undefined;
    const s1 = (data != null) ? getStr(data, 'speech') : undefined;
    const s2 = getStr(raw, 'speech');
    const s = s1 ?? s2 ?? '';
    return typeof s === 'string' && s.trim() !== '' ? s : '';
  };

  const {
    agentValidations,
    sessionByStatusAndAgent,
    nextContact,
    sessionById,
    saveContactNotes,
    ensureCanDial,
    playVmOnCall,
    invalidateContact,
    transferCall,
    setAction,
    markCutOff,
    markTransferError,
    callLogUpdate,
    callLogByCallId,
  } = useAgentApi();

  const tickRef = useRef<() => Promise<void>>(async () => {});
  const speechIntervalRef = useRef<number | null>(null);
  const hasBootstrappedFirstContactRef = useRef(false);
  const bootstrappedSessionIdRef = useRef<string | null>(null);
  const { t } = useTranslation();
  
  const getCurrentSidSafe = useCallback((): string | null => {
    const sidFromStore = useTwilioStore.getState().callSid ?? null;
    if (typeof sidFromStore === 'string' && sidFromStore.trim() !== '') {
      lastCallIdRef.current = sidFromStore;
      return sidFromStore;
    }

    const sidFromState = (typeof callSid === 'string' && callSid.trim() !== '') ? callSid : null;
    if (sidFromState != null) {
      lastCallIdRef.current = sidFromState;
      return sidFromState;
    }

    try {
      const c = currentCall?.() ?? useTwilioStore.getState().activeCall;
      const sidFromCall = extractCallSid(c);
      if (typeof sidFromCall === 'string' && sidFromCall.trim() !== '') {
        lastCallIdRef.current = sidFromCall;
        return sidFromCall;
      }
      return null;
    } catch {
      return null;
    }
  }, [callSid, currentCall]);


  const showContactLoader = useCallback((show: boolean) => {
    setContactLoading(show);
  }, []);

  const handleToggleMute = useCallback(
    async (next: boolean) => {
      if (isOnTransfer) {
        return;
      }
      try {
        const c = currentCall?.() ?? activeCall ?? null;
        if ((c == null) || !hasMute(c)) {
          await logWarning(t('agentLayout.toasts.warning.noCallToMute'), 'AgentsPage/handleToggleMute', {}, true);
          return;
        }
        c.mute(next);
        
        await logInfo(next
          ? t('agentLayout.toasts.info.microMuted')
          : t('agentLayout.toasts.info.microUnmuted'), 'AgentsPage/handleToggleMute',
        {}, 
        true
        );

        setMutedLocal(next);
        setIsMuted(next); // Sync with Zustand store
      } catch (e) {
        await logError(t('agentLayout.toasts.error.toggleMute'), 'AgentsPage/handleToggleMute', { error: getErrorMessage(e) }, true);
      }
    },
    [currentCall, activeCall, isOnTransfer, setIsMuted]
  );

  const getVMDuration = async (url: string): Promise<number> =>
    await new Promise(resolve => {
      const audio = new Audio(url);
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration * 1000);
      });
    });


  const handlePlayVoicemail = useCallback(async () => {
    const url = callSession?.voicemailUrl ?? null;
    if (url == null) {
      await logWarning(t('agentLayout.toasts.warning.noVM'), 'AgentsPage/handlePlayVoicemail', {}, true);
      return;
    }
    if (callSid == null) {
      await logWarning('No active callSid', 'AgentsPage/handlePlayVoicemail', { callSid }, false);
      return;
    }
    const { success, error } = await playVmOnCall({ friendlyNameVM: callSid, recordingUrl: url });
    if (!success) {
      await logError(t('agentLayout.toasts.error.playVM', { lng: 'en' }), 'AgentsPage/handlePlayVoicemail', { error: getErrorMessage(error?.message ?? 'playVmOnCall failed') }, false);
      showToast('error', t('agentLayout.toasts.error.playVM', 'Error while playing voicemail. Please log in again'));
      return;
    }
    setIsOnVoicemail(true);
    await logInfo(t('agentLayout.toasts.info.vmPlaying'), 'AgentsPage/handlePlayVoicemail', {}, true);

    const duration = await getVMDuration(url);
    setTimeout(() => {
      setIsOnVoicemail(false); 
    }, duration + 500);
    
  }, [callSession?.voicemailUrl, callSid, playVmOnCall]);

  const guardOnSessionChange = useCallback(
    async (raw: unknown) => {
      if (!isRec(raw)) {
        return;
      }

      const rawRec = isRec(raw) ? (raw ) : null;

      const status: string = (() => {
        const s = rawRec?.status;
        return typeof s === 'string' ? s.toLowerCase() : '';
      })();

      const isPaused = status === 'paused';
      const isFinished = status === 'finished';
      const isCutOff = status === 'cut_off';

      const actionObj: Record<string, unknown> | null =
        isRec(rawRec?.action) ? (rawRec.action ) : null;

      const actionStr: string = (() => {
        const a = actionObj?.action;
        return typeof a === 'string' ? a.toLowerCase() : '';
      })();

      const userIdStr: string | null = (() => {
        const u = actionObj?.userId;
        if (typeof u === 'string' || typeof u === 'number') {
          return String(u);
        }
        return null;
      })();

      const transferredByMe =
        actionStr === 'transferred' &&
        userIdStr != null &&
        agentId != null &&
        userIdStr === String(agentId);

      const transferredByOther =
        actionStr === 'transferred' &&
        userIdStr != null &&
        agentId != null &&
        userIdStr !== String(agentId);

      // The local ref is the authoritative half of this check. `action` is a
      // shared session field and the SDR's cleanup rewrites it to `paused` when
      // the transferred call ends — which stripped this protection from the very
      // agent who made the transfer, two minutes after the fact, and let them
      // mark their own handed-over call as cut off.
      if (callTransferredByMeRef.current || transferredByMe) {
        await logInfo('Ignoring teardown: this call was transferred by ME', 'AgentsPage/guardOnSessionChange', {
          event: 'transfer.teardown_skipped_own',
          callSessionId,
          agentId,
          status,
          actionStr,
        }, false);
        return;
      }

      if (isWithinLocalTransferWindow()) {
        await logInfo('Local transfer window (skip teardown)', 'AgentsPage/guardOnSessionChange', { status, actionStr }, false);
        return;
      }

      const shouldCut = isPaused || isFinished || isCutOff || transferredByOther;

      if (hasActiveCall && shouldCut) {
        const sidForModal = getCurrentSidSafe();
        if (typeof sidForModal === 'string' && sidForModal.trim() !== '') {
          lastCallIdRef.current = sidForModal;
          try {
            const { success } = await markCutOff(sidForModal);
            if (!success) {
              await logWarning('Failed to mark cut_off', 'AgentsPage/guardOnSessionChange', { sidForModal }, false);
            }
          } catch (e) {
            await logWarning(`markCutOff error: ${getErrorMessage(e)}`, 'AgentsPage/guardOnSessionChange', { error: e }, false);
          }
        } else {
          await logWarning('No CallSid available to mark cut_off', 'AgentsPage/guardOnSessionChange', { callSid }, false);
        }

        endCall();
        await logInfo(
          t('agentLayout.toasts.info.callEndedState', { lng: 'en' }), 
          'AgentsPage/guardOnSessionChange', 
          { status, actionStr }, 
          false
        );
        
        showToast('success', t('agentLayout.toasts.info.callEndedState', 'Call ended due to session state change'));
      }
    },
    [hasActiveCall, agentId, endCall, markCutOff, getCurrentSidSafe]
  );

  // ========= Speech (script) refresher =========
  // Refreshes the script text and nothing else. This used to also run
  // guardOnSessionChange, which tears calls down — and it does so from its own
  // 3s interval, with none of the transfer-hold suppression that protects the
  // polling tick. That is how an agent ended up cutting off a call it had
  // already handed over: the teardown check kept running throughout the
  // transfer, and fired the moment the SDR's cleanup rewrote the session.
  const loadSpeechById = useCallback(
    async (csId: string) => {
      const { success, data } = await sessionById(csId);
      if (success && (data != null) && isRec(data as unknown)) {
        const raw = data as unknown as Rec;
        const patch: Partial<CleanCallSession> = {};

        const speech = getSpeechFromRaw(raw);
        if (speech !== '') {
          patch.intentBody = speech;
        }

        const vm = getStr(raw, 'voicemailUrl');
        if (typeof vm === 'string' && vm.trim() !== '') {
          patch.voicemailUrl = vm.trim();
        }

        if (Object.keys(patch).length > 0) {
          mergeCallSession(patch);
        }
      }
    },
    [sessionById, mergeCallSession]
  );

  const startSpeechRefresh = useCallback((csId: string): void => {
    if (speechIntervalRef.current !== null) {
      window.clearInterval(speechIntervalRef.current);
    }

    loadSpeechById(csId).catch((e) => {
      void logWarning(`loadSpeechById error: ${getErrorMessage(e)}`, 'AgentsPage/startSpeechRefresh', { csId }, false);
    });

    speechIntervalRef.current = window.setInterval(() => {
      loadSpeechById(csId).catch((e) => {
        void logWarning(`loadSpeechById (interval) error: ${getErrorMessage(e)}`, 'AgentsPage/startSpeechRefresh', { csId }, false);
      });
    }, SPEECH_REFRESH_MS);
  }, [loadSpeechById]);

  const stopSpeechRefresh = useCallback(() => {
    if (speechIntervalRef.current != null) {
      window.clearInterval(speechIntervalRef.current);
      speechIntervalRef.current = null;
    }
  }, []);

  const fetchNextContact = useCallback(
    async (
      csId: string,
      aId: string,
      maxRetries = 4,
      opts?: { showLoader?: boolean; force?: boolean }
    ) => {
      const force = Boolean(opts?.force);

      if (!force && postTransferHoldRef.current) {
        await logInfo('Skip nextContact: post-transfer hold (no force)', 'AgentsPage/fetchNextContact', {}, false);
        return;
      }

      const showLoader = opts?.showLoader ?? true;
      showContactLoader(showLoader);

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const { success, data, error } = await nextContact(csId, aId);

        if (!success || data == null) {
          if (attempt === 4) {
            await logError(t('agentLayout.toasts.error.springContact'), 'AgentsPage/fetchNextContact',
              { error: getErrorMessage(error?.message ?? 'nextContact failed'), sessionId: callSessionId }, true);
          }

          continue;
        }

        interface NextContactPayload {
          body?: unknown;
          queueLength?: number;
        }

        const safe: NextContactPayload | null =
          typeof data === 'object' && data !== null
            ? (data as NextContactPayload)
            : null;

        const body: Record<string, unknown> =
          typeof safe?.body === 'object' && safe.body !== null
            ? (safe.body as Record<string, unknown>)
            : {};

        const q: number = typeof safe?.queueLength === 'number' ? safe.queueLength : 0;

        const newContact: UiContact = {
          id: typeof body.id === 'string' ? body.id : undefined,
          name: typeof body.name === 'string' ? body.name : undefined,
          jobTitle: typeof body.jobTitle === 'string' ? body.jobTitle : undefined,
          email: typeof body.email === 'string' ? body.email : undefined,
          phone: typeof body.phone === 'string' ? body.phone : undefined,
          accountName: typeof body.accountName === 'string' ? body.accountName : undefined,
          fuegoLead: Boolean(body.fuegoLead),
          notes: typeof body.notes === 'string' ? body.notes : null,
          notesAgent: typeof body.notesAgent === 'string' ? body.notesAgent : null,
          notesSDR: typeof body.notesSDR === 'string' ? body.notesSDR : null,
          contactListId: typeof body.contactListId === 'string' ? body.contactListId : undefined,
        };

        if (
          attempt < maxRetries &&
          (newContact.id != null) &&
          newContact.id === currentContact?.id &&
          newContact.contactListId !== callSession?.contactList
        ) {
          await logWarning(
            'nextContact returned same contact, retrying...',
            'AgentsPage/fetchNextContact',
            { attempt },
            false,
          );
          continue;
        }

        setCurrentContact(newContact);
        setQueueLeft(q);

        await logInfo(t('agentLayout.toasts.info.nextContact'), 'AgentsPage/fetchNextContact', { queueLeft: q, attempt }, true);

        if (newContact.id != null) {
          showContactLoader(false);
        }

        break;
      }
    },
    [nextContact, currentContact?.id, logError, logInfo, showContactLoader, callSessionId]
  );

  useEffect(() => {
    if (typeof callSid === 'string' && callSid.trim() !== '') {
      lastCallIdRef.current = callSid;
    }
  }, [callSid]);

  const ensureCallSid = useCallback(async (): Promise<string | null> => {
    for (let i = 0; i < 10; i += 1) {
      const sid =
        lastCallIdRef.current ??
        useTwilioStore.getState().callSid ??
        getCurrentSidSafe();
      if (typeof sid === 'string' && sid.trim() !== '') {
        lastCallIdRef.current = sid;
        return sid;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 150));
    }
    return null;
  }, [getCurrentSidSafe]);

  const prefillAgentNotes = useCallback((): string => {
    const parts: string[] = [];
    const a = (currentContact?.notesAgent ?? '').trim();
    if (a !== '') {
      parts.push(a);
    }
    return parts.join('\n');
  }, [currentContact]);

  const handleDispositionSubmit = useCallback(async (): Promise<void> => {
    // Only the snapshot taken when the modal opened is trusted here. Live
    // sources (lastCallIdRef, the Twilio store, currentContact) may already
    // belong to the next call by the time the agent presses Submit.
    const target = dispositionTarget;
    setSubmitDisabled(true);

    try {
      const safeCallId = (target?.callId ?? '').trim();
      const hasCallId = safeCallId !== '';

      // Without a sid there is no call log to write to. The notes and the
      // contact rotation below still happen, so the agent is not stuck on the
      // same contact (and never redials it) because one call failed early.
      if (!hasCallId) {
        await logError(
          t('agentLayout.toasts.error.updateDisposition'),
          'AgentsPage/handleDispositionSubmit',
          { reason: 'no callId for this call', target, callSessionId, disposition: dispositionValue },
          true
        );
      }

      const preserveStatuses = new Set(['voicemail', 'transferred']);
      let statusToSend: string = 'finished';

      if (hasCallId) {
        try {
          const logRes = await callLogByCallId(safeCallId);
          const currentStatus = (
            logRes?.success && logRes?.data?.status != null
              ? logRes.data.status
              : ''
          ).trim();

          if (preserveStatuses.has(currentStatus)) {
            statusToSend = currentStatus;
          }

          await logInfo(
            'Fetched current call log for disposition',
            'AgentsPage/handleDispositionSubmit',
            {
              callId: safeCallId,
              fetchSuccess: logRes?.success,
              currentStatus,
              statusToSend,
            },
            false
          );
        } catch (e) {
          await logWarning(
            `callLogByCallId failed: ${getErrorMessage(e)}`,
            'AgentsPage/handleDispositionSubmit',
            { callId: safeCallId },
            false
          );
        }
      }

      const targetContactId = target?.contactId ?? null;
      if (targetContactId != null && targetContactId !== '') {
        const notesPayload: UpdateNotesPayload = {
          contactId: targetContactId,
          callId: safeCallId,
          notesAgent: agentNotes,
        };

        try {
          await saveContactNotes(notesPayload);
        } catch (e) {
          await logWarning(
            `saveContactNotes failed: ${getErrorMessage(e)}`,
            'AgentsPage/handleDispositionSubmit',
            { session: callSession, notes: notesPayload },
            false
          );
        }
      }

      if (hasCallId) {
        const payload = {
          callId: safeCallId,
          status: statusToSend,
          disposition: dispositionValue,
        };

        await logInfo(
          'Sending disposition update to API',
          'AgentsPage/handleDispositionSubmit',
          payload,
          false
        );

        try {
          const res = await callLogUpdate(payload);
          if (res == null || !res.success) {
            await logError(
              'Failed to update call log',
              'AgentsPage/handleDispositionSubmit',
              { callId: safeCallId, res },
              false
            );
          } else {
            await logInfo(
              'Call log updated successfully',
              'AgentsPage/handleDispositionSubmit',
              { safeCallId, payload },
              false
            );
          }
        } catch (e) {
          await logError(
            `callLogUpdate exception: ${getErrorMessage(e)}`,
            'AgentsPage/handleDispositionSubmit',
            { payload },
            false
          );
        }
      }

      const targetSessionId = target?.callSessionId ?? callSessionId ?? null;
      if (targetSessionId != null && agentId != null) {
        try {
          await invalidateContact(targetSessionId, agentId);
        } catch (e) {
          await logError(
            `invalidateContact failed: ${getErrorMessage(e)}`,
            'AgentsPage/handleDispositionSubmit',
            { error: e },
            false
          );
        }
      }

      if (callSessionId != null && agentId != null) {
        try {
          await fetchNextContact(callSessionId, agentId, 4, { force: true });
        } catch (e) {
          await logWarning(
            `fetchNextContact on disposition submit failed: ${getErrorMessage(e)}`,
            'AgentsPage/handleDispositionSubmit',
            {},
            false
          );
        }
      }
    } catch (globalError) {
      await logError(
        `Unhandled exception in handleDispositionSubmit: ${getErrorMessage(globalError)}`,
        'AgentsPage/handleDispositionSubmit',
        {},
        false
      );
    } finally {
      // The modal is non-dismissable, so it is released here no matter what
      // happened above: missing sid, failed API calls or a thrown error.
      setShowDisposition(false);
      setDispositionValue('');
      setAgentNotes('');
      setShowSpecify(false);
      setDispositionTarget(null);
      setSubmitDisabled(false);

      // A sid that has been dispositioned must never be picked up again as a
      // fallback for a later call (e.g. one that failed before Twilio reported
      // its own sid), or that call's submit would rewrite this one's log.
      if (target?.callId != null && lastCallIdRef.current === target.callId) {
        lastCallIdRef.current = null;
      }
    }
  }, [
    dispositionTarget,
    agentId,
    callSessionId,
    callLogUpdate,
    invalidateContact,
    callLogByCallId,
    saveContactNotes,
    fetchNextContact,
    dispositionValue,
    agentNotes,
    logWarning,
    logError,
    logInfo,
    callSession,
  ]);

  const handleDispositionSubmitVoid = useCallback((): void => {
    void handleDispositionSubmit().catch(async (e) => {
      await logError(t('agentLayout.toasts.error.updateDisposition'), 'AgentsPage/DispositionsModal.onSubmit', { error: getErrorMessage(e) }, true);
    });
  }, [handleDispositionSubmit]);

  const onDispositionChange = useCallback((v: string) => {
    setDispositionValue(v);
    setShowSpecify(v === 'OTR');
  }, []);

  // ========= Watch hasActiveCall changes to detect remote hangup =========
  const prevActiveRef = useRef<boolean>(hasActiveCall);

  useEffect(() => {
    const justFinishedRemotely = prevActiveRef.current && !hasActiveCall;

    if (justFinishedRemotely) {
      setMutedLocal(false);
      setIsMuted(false); // Sync with Zustand store
      setIsOnTransfer(false);
    }

    if (justFinishedRemotely && (callSessionId != null) && (agentId != null)) {
      (async () => {
        if (callTransferredByMeRef.current) {
          postTransferHoldRef.current = true;
          callTransferredByMeRef.current = false;

          try {
            await invalidateContact(callSessionId, agentId);
          } catch { }
          needsNextContactOnResumeRef.current = true;
          await logInfo('Remote hangup after MY transfer → deferred contact rotation to session resume', 'AgentsPage/useEffect[hasActiveCall]', {}, false);
          setTimeout(() => {
            postTransferHoldRef.current = false;
          }, 150);
          return;
        }

        if (lastCallIdRef.current == null) {
          const sid = getCurrentSidSafe();
          if (sid != null) {
            lastCallIdRef.current = sid;
          }
        }
        // Pin the target now, while the ref still holds the call that just
        // ended. If a disposition is already pending, keep it: what the agent
        // is looking at must be what gets written.
        const endedCallId = lastCallIdRef.current;
        setDispositionTarget((prev) => prev ?? {
          callId: endedCallId,
          contactId: currentContact?.id ?? null,
          contactName: currentContact?.name ?? null,
          callSessionId,
        });
        setAgentNotes((prev) => (prev?.trim() !== '' ? prev : prefillAgentNotes()));
        setShowDisposition(true);
        await logInfo('Remote hangup (no transfer by me) → show dispositions', 'AgentsPage/useEffect[hasActiveCall]', { callId: endedCallId }, false);
      })().catch(() => {});
    }

    prevActiveRef.current = hasActiveCall;
  }, [
    hasActiveCall,
    callSessionId,
    agentId,
    currentContact?.id,
    currentContact?.name,
    invalidateContact,
    getCurrentSidSafe,
    prefillAgentNotes,
    setIsMuted,
  ]);

  const tick = useCallback(async () => {
    if (tickRunningRef.current) {
      return;
    }

    tickRunningRef.current = true;

    try {
      if (postTransferHoldRef.current || callTransferredByMeRef.current) {
        await logInfo('Tick suppressed during transfer hold', 'AgentsPage/tick', {}, false);
        return;
      }

      if (agentId == null) {
        await logError('(Agent) Missing agentId in session', 'AgentsPage/tick', {}, false);
        return;
      }

      const suppressTeardown = (): boolean =>
        postTransferHoldRef.current || callTransferredByMeRef.current;

      try {
        const { success, data } = await agentValidations(agentId);
        if (!success || (data == null)) {
          if (suppressTeardown()) {
            await logInfo('Suppress teardown (validation failed) during transfer hold', 'AgentsPage/tick', {}, false);
            return;
          }
          await logError('{Agent} Validation request failed', 'AgentsPage/tick', {}, false);
          setStarted(false);
          setCallSession(null);
          stopSpeechRefresh();
          return;
        }

        const validations = asPartialValidations(data);

        if (userRaw?.sdrId !== null && validations.sdr !== null && userRaw?.sdrId !== validations.sdr?.data?.id) {
          setUserRaw({
            id: currentUser?.userId,
            sdrId: validations?.sdr?.data?.id,
            organizationId: validations.sdr?.data?.organizationId,
            sdrName: validations.sdr?.data?.sdrName,
            organizationName: validations.sdr?.data?.organizationName ?? '',
            userId: currentUser?.userId,
          });
        }

        mergeCallSession({
          sdrId: validations?.sdr?.data?.id ?? null,
          sdrName: validations?.sdr?.data?.sdrName ?? null,
          organizationId: validations?.sdr?.data?.organizationId ?? null,
          organizationName: validations?.sdr?.data?.organizationName ?? null,
          ...(((validations?.voiceNote?.data?.url) != null) ? { voicemailUrl: validations.voiceNote.data.url } : {}),
        });

        const cs = validations?.callSession?.data;

        if (cs?.id !== callSession?.id) {
          setCallSessionId(cs?.id ?? null);
        }

        const newSdrId = validations.sdr?.data?.sdrId ?? null;
        if (newSdrId !== null) {
          setCurrentSdrId(newSdrId);
        }

        if (validations?.validationsPassed === false) {
          await logInfo('(Agent) Validations not passed yet…', 'AgentsPage/tick', {}, false);
          if (suppressTeardown()) {
            await logInfo('Suppress teardown (validations not passed) during transfer hold', 'AgentsPage/tick', {}, false);
            return;
          }
          setStarted(false);
          setSessionPaused(false);
          setCallSession(null);
          stopSpeechRefresh();
          return;
        }

        // Detect paused session before treating it as active
        if ((cs?.id) != null && cs?.status === 'paused') {
          if (suppressTeardown()) {
            await logInfo('Suppress teardown (paused session) during transfer hold', 'AgentsPage/tick', {}, false);
            return;
          }
          if (useTwilioStore.getState().hasActiveCall) {
            endCall();
          }
          // Update the store status so useEffect[callSession.status] doesn't fight back
          mergeCallSession({ status: 'paused' });
          setStarted(false);
          setSessionPaused(true);
          stopSpeechRefresh();
          await logInfo('(Agent) Session is paused. Showing paused screen.', 'AgentsPage/tick', {}, false);
          return;
        }

        const isStarted = Boolean(validations?.callSession?.isValid) && cs?.status === 'started';

        const handleSession = async (sessionId: string): Promise<void> => {
          const full = await sessionById(sessionId);

          if (full.success && (full.data != null) && isRec(full.data as unknown)) {
            const raw = full.data as unknown as Rec;

            const patch: Partial<CleanCallSession> = {
              id: getStr(raw, 'id') ?? sessionId,
              status: getStr(raw, 'status'),
              scriptId: getStr(raw, 'scriptId'),
              scriptTitle: getStr(raw, 'scriptTitle'),
              sdrId: getStr(raw, 'sdrId') ?? (validations?.sdr?.data?.sdrId ?? null),
              sdrName: getStr(raw, 'sdrName') ?? (validations?.sdr?.data?.sdrName ?? null),
              sdrStatus: getStr(raw, 'sdrStatus') ?? null,
              organizationId: getStr(raw, 'organizationId') ?? (validations?.sdr?.data?.organizationId ?? null),
              organizationName: getStr(raw, 'organizationName') ?? (validations?.sdr?.data?.organizationName ?? null),
              agents: getArrStr(raw, 'agents'),
              voiceDropsIds: getArrStr(raw, 'voiceDropsIds'),
              contactList: Array.isArray(raw.contactList) ? (raw.contactList as CleanCallSession['contactList']) : null,
              notes: Array.isArray(raw.notes) ? (raw.notes as string[]) : null,
              action: isRec(raw.action) ? (raw.action as CleanCallSession['action']) : null,
              voiceNoteId: getStr(raw, 'voiceNoteId') ?? null,
              ...(() => {
                const url = getStr(raw, 'voicemailUrl');
                const u = (typeof url === 'string') ? url.trim() : '';
                return u !== '' ? { voicemailUrl: u } : {};
              })(),
              ...(() => {
                const speech = getSpeechFromRaw(raw);
                return (speech !== '') ? { intentBody: speech } : {};
              })(),
            };

            mergeCallSession(patch);
            await guardOnSessionChange(raw);
          }

          if (!startedRef.current) {
            setStarted(true);
          }
          setSessionPaused(false);
          startSpeechRefresh(sessionId);

          // Detect session change: if the session ID differs from the one we
          // bootstrapped, reset the flag so we fetch a fresh contact for the new session.
          if (bootstrappedSessionIdRef.current !== sessionId) {
            hasBootstrappedFirstContactRef.current = false;
            bootstrappedSessionIdRef.current = sessionId;
          }

          if (needsNextContactOnResumeRef.current) {
            // Primary path: session was paused and is now resuming → rotate contact.
            // Safety fallback: if the pause→resume cycle was somehow missed and the
            // session is already started, still consume the flag so the contact rotates.
            needsNextContactOnResumeRef.current = false;
            hasBootstrappedFirstContactRef.current = true;
            await fetchNextContact(sessionId, agentId);
            return;
          }

          if (!hasBootstrappedFirstContactRef.current) {
            await fetchNextContact(sessionId, agentId);
            hasBootstrappedFirstContactRef.current = true;
          }
        };

        if (isStarted && ((cs?.id) != null)) {
          setSessionPaused(false);
          await handleSession(cs.id);
          return;
        }

        const res = await sessionByStatusAndAgent('started', agentId);

        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          const foundId = typeof res.data[0]?.id === 'string' ? res.data[0].id : null;
          if (foundId != null) {
            setSessionPaused(false);
            await handleSession(foundId);
            return;
          }
        }

        if (suppressTeardown()) {
          await logInfo('Suppress teardown (no active session) during transfer hold', 'AgentsPage/tick', {}, false);
          return;
        }

        setStarted(false);
        setSessionPaused(false);
        setCallSession(null);
        stopSpeechRefresh();
        await logInfo('(Agent) No active session. Waiting to be started…', 'AgentsPage/tick', {}, false);
      } catch (err) {
        if (postTransferHoldRef.current || callTransferredByMeRef.current) {
          await logInfo('Suppress teardown (unexpected error) during transfer hold', 'AgentsPage/tick', {}, false);
          return;
        }
        await logError(`{Agent} Unexpected error validating session: ${getErrorMessage(err)}`, 'AgentsPage/tick', {}, false);
        setStarted(false);
        setCallSession(null);
        stopSpeechRefresh();
      }
    } finally {
      tickRunningRef.current = false;
    }
  }, [
    agentId,
    callSessionId,
    agentValidations,
    sessionById,
    sessionByStatusAndAgent,
    fetchNextContact,
    startSpeechRefresh,
    stopSpeechRefresh,
    mergeCallSession,
    guardOnSessionChange,
    setCallSession,
  ]);

  useEffect(() => {
    startedRef.current = started;
    if (!started) {
      hasBootstrappedFirstContactRef.current = false;
      bootstrappedSessionIdRef.current = null;
    }
  }, [started]);

  useEffect(() => {
    tickRef.current = tick;
  }, [tick, callSession]);

  useEffect(() => {
    const interval = started ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS;

    const id = window.setInterval(() => {
      Promise.resolve(tickRef.current?.()).catch((e) => {
        void logWarning(`tickRef.current() error: ${getErrorMessage(e)}`, 'AgentsPage/useEffect[tick]', {}, false);
      });
    }, interval);

    Promise.resolve(tickRef.current?.()).catch((e) => {
      void logWarning(`tickRef.current() initial error: ${getErrorMessage(e)}`, 'AgentsPage/useEffect[tick-init]', {}, false);
    });

    return () => {
      window.clearInterval(id);
    };
  }, [started]);

  useEffect(() => {
    if (twilioStatus === 'ready' || twilioStatus === 'registering') {
      void tickRef.current();
    }
  }, [twilioStatus]);

  useEffect(() => {
    if (agentId != null) {
      void tickRef.current();
    }
  }, [agentId, callSessionId]);

  useEffect(() => () => {
    stopSpeechRefresh();
  }, [stopSpeechRefresh]);

  useEffect(() => {
    if ((callSessionId == null) && started && !postTransferHoldRef.current && !callTransferredByMeRef.current) {
      setStarted(false);
      return;
    }

    const finishedOrCut =
      callSession?.status === 'finished' ||
      callSession?.status === 'cut_off';

    // Paused is handled by tick() — only set the flag, don't teardown
    if (callSession?.status === 'paused') {
      setSessionPaused(true);
      return;
    }

    if (finishedOrCut) {
      const actionRaw = (callSession?.action as Record<string, unknown> | null) ?? null;
      const actionStr = typeof actionRaw?.action === 'string' ? actionRaw.action.toLowerCase() : '';
      const userIdStr = actionRaw?.userId != null ? String(actionRaw.userId) : null;
      const byMe = userIdStr != null && agentId != null && userIdStr === String(agentId);
      const isMyTransfer = actionStr === 'transferred' && byMe;

      if (!isMyTransfer && !postTransferHoldRef.current && !callTransferredByMeRef.current) {
        endCall();
        setStarted(false);
        setSessionPaused(false);
        void logInfo(t('agentLayout.toasts.info.sessionFinished'), 'AgentsPage/useEffect[status]', { ctx: 'Session finished/cut_off. Back to pre-session.' }, true);
      }
      return;
    }

    if (!started && callSession?.status === 'started') {
      setStarted(true);
      setSessionPaused(false);
    }
  }, [callSession?.status, callSessionId, started, endCall, agentId, callSessionId]);

  const isPaused: boolean = callSession?.status !== 'started';

  const actionObj: Rec | null = isRec(callSession?.action) ? (callSession.action ) : null;
  const actionStr: string =
    typeof actionObj?.action === 'string' ? (actionObj.action ).toLowerCase() : '';

  const isTransferredAction: boolean = actionStr === 'transferred';

  const sdrStatus = callSession?.sdrStatus;
  const isSdrBusy: boolean = typeof sdrStatus === 'string' && sdrStatus !== 'free';

  // A transfer in flight reads (action=transferred, sdrStatus=busy, status=paused):
  // the agent flags the session and the SDR's screen answers with the other two.
  // The combination below cannot belong to one, so it means the SDR side never
  // picked the transfer up and the flag was left behind. Nothing in the UI can
  // clear it — the agent's transfer button is disabled by the very flag whose
  // failure path would reset it, and the SDR is only offered Resume while the
  // session reads as paused — so the session stays locked until someone edits
  // the database by hand.
  const isOrphanTransferFlag: boolean =
    isTransferredAction && !isSdrBusy && callSession?.status === 'started';

  useEffect(() => {
    if (!isOrphanTransferFlag) {
      orphanTransferSinceRef.current = null;
      setTransferLockReleased(false);
      return;
    }

    if (orphanTransferSinceRef.current === null) {
      orphanTransferSinceRef.current = Date.now();
    }

    const release = async (): Promise<void> => {
      // Releasing the lock is safe for everyone: the flag is session-wide, so a
      // stale one blocks every agent and every one of them has to be let go.
      setTransferLockReleased(true);

      // Ending the call is NOT. This effect reads session fields, so it fires in
      // every agent's browser — including agents mid-conversation who never
      // transferred anything. Only the agent who made the transfer owns the
      // stranded call; hanging up anyone else would cut a live call and stamp
      // someone else's contact as a failed transfer.
      if (!callTransferredByMeRef.current) {
        void logWarning(
          'Releasing a stale transfer lock (this agent did not transfer)',
          'AgentsPage/orphanTransferFlag',
          { event: 'transfer.lock_released_bystander', callSessionId, agentId, sdrStatus, actionStr },
          false
        );
        return;
      }

      // A lost transfer is a failed transfer, so the call is classified here
      // rather than asking the agent for a disposition. `transfer_error` is
      // deliberately the same status the server writes from Twilio's callback,
      // so the contact never carries two conflicting outcomes in a metrics trail
      // that is append-only.
      //
      // Leaving the agent muted on a dead-air call is not a recovery: by this
      // point the client has been listening to silence since the handoff.
      // Hanging up is safe even if this ever fires on a transfer that did go
      // through — transferCall sets `endConferenceOnExit: false` on the agent
      // participant, so dropping their leg never ends a live conference.
      const sid = getCurrentSidSafe();
      if (typeof sid === 'string' && sid.trim() !== '') {
        lastCallIdRef.current = sid;
        try {
          await markTransferError(sid);
        } catch (e) {
          void logWarning(`markTransferError failed: ${getErrorMessage(e)}`, 'AgentsPage/orphanTransferFlag', {
            event: 'transfer.classify_failed',
            callSessionId,
            agentId,
            conferenceId: sid,
          }, false);
        }
      }

      setIsOnTransfer(false);

      // `callTransferredByMeRef` is deliberately left set: the hangup watcher
      // reads it to take the post-transfer branch, which rotates to the next
      // contact WITHOUT opening the disposition modal — the call is already
      // classified above. That branch clears both refs itself.
      endCall();

      void logWarning(
        'Ending a call whose transfer the SDR never picked up',
        'AgentsPage/orphanTransferFlag',
        {
          event: 'transfer.lock_released_owner',
          callSessionId,
          agentId,
          conferenceId: lastCallIdRef.current,
          contactId: currentContact?.id,
          sdrStatus,
          actionStr,
        },
        false
      );
    };

    const elapsed = Date.now() - orphanTransferSinceRef.current;
    if (elapsed >= ORPHAN_TRANSFER_GRACE_MS) {
      void release();
      return;
    }

    const timerId = window.setTimeout(() => {
      void release();
    }, ORPHAN_TRANSFER_GRACE_MS - elapsed);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isOrphanTransferFlag, callSessionId, sdrStatus, actionStr, endCall, getCurrentSidSafe, markTransferError]);

  const transferLocked =
    !transferLockReleased &&
    (callTransferredByMeRef.current || isTransferredAction);

  const canDialNow = !isPaused && !transferLocked && !isSdrBusy;
  const canUseMute = hasActiveCall && canDialNow && !isOnTransfer;
  const canUseTransfer = hasActiveCall && canDialNow;

  const [isOnVoicemail, setIsOnVoicemail] = useState(false);

  const handleTransfer = useCallback(async () => {
    if (!hasActiveCall || (callSid == null) || (callSessionId == null) || (agentId == null)) {
      await logWarning(t('agentLayout.toasts.warning.noActiveCall'), 'AgentsPage/handleTransfer', {}, true);
      return;
    }

    const finalSdrId = currentSdrId === '' ? String(userRaw?.sdrId ?? '') : currentSdrId;

    // Without an SDR there is nobody to transfer to. This used to log and carry
    // on, sending an empty sdrId to the edge service.
    if (finalSdrId === '') {
      await logError(t('agentLayout.toasts.error.sdrLost'), 'AgentsPage/handleTransfer', {
        event: 'transfer.no_sdr',
        callSessionId,
        agentId,
        conferenceId: callSid,
        contactId: currentContact?.id,
      }, true);
      return;
    }

    try {
      transferLocalWindowRef.current = Date.now();

      const res = await transferCall({
        callId: String(callSid),
        sdrId: finalSdrId,
        organizationId: String(userRaw?.organizationId ?? ''),
        callSessionId: String(callSessionId),
        agentId: String(agentId),
      });

      if (!res.success) {
        await logError('Transfer failed', `agents-${handleTransfer.name}`, { error:res?.error?.message }, false );
      }

      // Written only once the transfer is accepted. `action` is a session-wide
      // field every agent polls, and a `transferred` value tears their live calls
      // down — so writing it up front meant a transfer that failed still cut
      // everyone off. Landing it here also removes the revert that used to run on
      // failure, which could overwrite another agent's legitimate transfer.
      await setAction(callSessionId, { action: 'transferred', userId: String(agentId) });

      try {
        const c = currentCall?.() ?? useTwilioStore.getState().activeCall;
        if (hasMute(c)) {
          c.mute(true);
        }
      } catch {}
      setMutedLocal(true);
      setIsMuted(true); // Sync with Zustand store
      setIsOnTransfer(true);

      callTransferredByMeRef.current = true;
      postTransferHoldRef.current = true;

      await logInfo(t('agentLayout.toasts.info.transferring'), 'AgentsPage/handleTransfer', {
        event: 'transfer.accepted',
        callSessionId,
        agentId,
        sdrId: finalSdrId,
        conferenceId: callSid,
        contactId: currentContact?.id,
      }, true);
    } catch (e) {
      await logError(getErrorMessage(e), 'AgentsPage/handleTransfer', {
        event: 'transfer.failed',
        callSessionId,
        agentId,
        sdrId: finalSdrId,
        conferenceId: callSid,
        contactId: currentContact?.id,
      }, true);
    }
  }, [hasActiveCall, callSid, callSessionId, agentId, transferCall, setAction, currentCall, userRaw, setIsMuted, currentSdrId, callSession, currentUser]);

  const handleDialpadStart = useCallback((num: string): void => {
    Promise.resolve((async () => {
      const to = (num ?? '').trim();

      try {
        await ensureCanDial({
          agentId,
          callSessionId,
          contactId: currentContact?.id ?? null,
          contactListId: currentContact?.contactListId ?? null,
          number: to,
        });
      } catch (err) {
        await logWarning(`ensureCanDial: ${getErrorMessage(err)}`, 'AgentsPage/Dialpad.onCallStart', { num: to }, false);
        return;
      }

      await logInfo(t('agentLayout.toasts.info.dialingTo',  { number:to }), 'AgentsPage/Dialpad.onCallStart', { num: to }, true);

      await startCall(to, {
        agent: String(agentId ?? ''),
        number: to,
        callSessionId: String(callSessionId ?? ''),
        contactId: String(currentContact?.id ?? ''),
        contactListId: String(currentContact?.contactListId ?? ''),
        To: to,
        agentId: String(agentId ?? ''),
      });

      const sid = await ensureCallSid();
      if (sid != null) {
        lastCallIdRef.current = sid;
      }
    })()).catch(async (e) => {
      await logError(t('agentLayout.toasts.error.startCall'), 'AgentsPage/Dialpad.onCallStart', { error: getErrorMessage(e) }, true);
    });
  }, [
    agentId,
    callSessionId,
    currentContact?.id,
    currentContact?.contactListId,
    ensureCanDial,
    startCall,
    ensureCallSid,
  ]);

  const handleDialpadEnd = useCallback((): void => {
    Promise.resolve((async () => {
      const sidBefore = (await ensureCallSid()) ?? undefined;

      await logInfo(t('agentLayout.toasts.info.callEnded'), 'AgentsPage/Dialpad.onCallEnd', {}, true);
      endCall();
      setMutedLocal(false);
      setIsMuted(false); // Sync with Zustand store
      setIsOnTransfer(false);

      if (callTransferredByMeRef.current || postTransferHoldRef.current) {
        return;
      }

      if ((lastCallIdRef.current == null) && typeof sidBefore === 'string' && sidBefore.trim() !== '') {
        lastCallIdRef.current = sidBefore;
      }

      // `sidBefore` was resolved before endCall(), so it belongs to the call
      // that was just hung up. Keep an already pending target (see the remote
      // hangup watcher, which also fires after endCall()).
      setDispositionTarget((prev) => prev ?? {
        callId: sidBefore ?? lastCallIdRef.current,
        contactId: currentContact?.id ?? null,
        contactName: currentContact?.name ?? null,
        callSessionId,
      });
      setAgentNotes((prev) => (((prev?.trim()) !== '') ? prev : prefillAgentNotes()));
      setShowDisposition(true);
    })()).catch(async (e) => {
      await logError(t('agentLayout.toasts.error.endCall'), 'AgentsPage/Dialpad.onCallEnd', { error: getErrorMessage(e) }, true);
    });
  }, [endCall, ensureCallSid, prefillAgentNotes, currentContact?.id, currentContact?.name, callSessionId]);

  const handleSendDTMF = useCallback((digit: string): void => {
    try {
      sendDigits(digit);
    } catch (e) {
      void logWarning(`sendDigits failed: ${getErrorMessage(e)}`, 'AgentsPage/Dialpad.onSendDTMF', { digit }, false);
    }
  }, [sendDigits]);

  const handlePlayVoicemailClick = useCallback((): void => {
    Promise.resolve(handlePlayVoicemail()).catch(async (e) => {
      await logWarning(`playVoicemail failed: ${getErrorMessage(e)}`, 'AgentsPage/Dialpad.onPlayVoicemail', {}, false);
    });
  }, [handlePlayVoicemail]);


  const [fuegoLead, setFuegoLead] = useState(false);

  async function handleAgentNotesFromCard (text: string): Promise<void> {
    if ((currentContact?.id) == null || text === currentContact.notesAgent || text === '') {
      return;
    }

    const { success, error } = await saveContactNotes({
      contactId: currentContact.id,
      callId: callIdRef.current !== '' ? callIdRef.current : '',
      notesAgent: text
    });

    if (!success) {
      await logError(t('agentLayout.toasts.error.saveNote'), 'AgentsPage/NotesCard.onSaveAgentNote',

        { error: getErrorMessage(error?.message ?? 'Failed to save agent note') }, true);
      return;
    }

    setAgentNotes(text);
    await logInfo(t('agentLayout.toasts.info.agentNoteSaved'), 'AgentsPage/NotesCard.onSaveAgentNote', {}, true);
  }

  useEffect(() => {
    setFuegoLead(Boolean(currentContact?.fuegoLead));
  }, [currentContact?.id]);

  async function getCallId (): Promise<void> {
    const safeCallId = await ensureCallSid();
    if (safeCallId != null) {
      callIdRef.current = safeCallId;
    }
  }

  useEffect(()=>{
    if (!hasActiveCall) {
      callIdRef.current = ''; 
    } else {
      void getCallId();
    }
  }, [callIdRef, hasActiveCall]);

  if (!started) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0">
          {sessionPaused ? (
            <PausedSessionScreen className="h-full" isTransfer={isTransferredAction} />
          ) : (
            <PreSessionScreen
              className="h-full"
              onStart={() => {
                Promise.resolve(tickRef.current?.()).catch((e) => {
                  void logWarning(`tickRef.current() error: ${getErrorMessage(e)}`, 'AgentsPage/PreSessionScreen.onStart', {}, false);
                });
              } }
              loading={false} 
            />
          )}
        </div>

        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pb-6">
          <Card className="w-full bg-muted/50 border border-border shadow-sm rounded-lg px-4 py-2">
            <CardContent className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 px-0 pt-2 text-xs">
              <div className="flex items-center gap-2">
                <User className="size-4 text-muted-foreground" />
                <span><span className="font-medium">{t('agentLayout.sdr', 'User')}:</span> {userRaw?.sdrName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-muted-foreground" />
                <span><span className="font-medium">{t('agentLayout.organization', 'Organization')}:</span> {userRaw?.organizationName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Key className="size-4 text-muted-foreground" />
                <span><span className="font-medium">Twilio:</span> {twilioStatus}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <DispositionsModal
          id={dispositionTarget?.contactId ?? ''}
          name={dispositionTarget?.contactName ?? '—'}
          open={showDisposition}
          onOpenChange={setShowDisposition}
          options={DISPOSITION_OPTIONS}
          value={dispositionValue}
          onChange={onDispositionChange}
          agentNotes={agentNotes}
          onNotesChange={setAgentNotes}
          showAgentNotes={true}
          showSpecify={showSpecify}
          submitDisabled={submitDisabled}
          fuegoLead={fuegoLead}
          onFuegoLeadChange={setFuegoLead}
          onSubmit={handleDispositionSubmitVoid}
          showFuegoLead={true}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 md:gap-3 flex-1 min-h-0">
      <DialerLayout isTransferred={isOnTransfer}>
        {!canDialNow && (
          <div className="md:col-span-3 flex items-center justify-center rounded-md bg-[var(--warning)] text-white text-xs md:text-sm px-3 py-1.5">
            {!canDialNow && isPaused && t('agentLayout.pausedCall', 'Session paused: dialing is disabled.')}
            {!canDialNow && transferLocked && t('agentLayout.transferredCall', 'Transfer in progress: dialing is disabled.')}
            {!canDialNow && isSdrBusy && t('agentLayout.busySDR', 'User busy: dialing is disabled.')}
          </div>
        )}

        <DialerBodyGate loading={contactLoading} mode="overlay" minHeight={420}>
          <ContactCard
            id={currentContact?.id ?? ''}
            name={currentContact?.name ?? '—'}
            jobTitle={currentContact?.jobTitle ?? '—'}
            email={currentContact?.email ?? '—'}
            phone={currentContact?.phone ?? '—'}
            account={currentContact?.accountName ?? '—'}
            fuegoLead={fuegoLead}
            onFuegoLeadChange={setFuegoLead}
            showFuegoLead={true}
            isTransferred={isOnTransfer}
            activeVoicemail={isOnVoicemail}
            isSDR={false}
            sdrName={currentUser?.sdrName}
            externalId={''}
          />

          <IntentCard isTransferred={isOnTransfer} activeVoicemail={isOnVoicemail} speech={intentBody ?? ''} />

          <fieldset disabled={transferLocked} className={transferLocked ? 'max-h-[550px] opacity-60 pointer-events-none select-none' : 'h-full'}>
            <Dialpad
              number={currentContact?.phone ?? ''}
              active={hasActiveCall}
              muted={muted}
              onToggleMute={canUseMute ? handleToggleMute : undefined}
              onTransfer={canUseTransfer ? handleTransfer : undefined}
              onCallStart={handleDialpadStart}
              onCallEnd={canDialNow ? handleDialpadEnd : undefined}
              onSendDTMF={handleSendDTMF}
              onPlayVoicemail={canDialNow ? handlePlayVoicemailClick : undefined}
              activeVoicemail = {isOnVoicemail}
              hasVoicemail={typeof callSession?.voicemailUrl === 'string' && callSession.voicemailUrl.trim() !== ''}
            />
          </fieldset>

          <NotesCard
            key={currentContact?.id ?? 'no-contact'}
            contactId={currentContact?.id ?? null}
            note={currentContact?.notes ?? null}
            agentNote={currentContact?.notesAgent ?? null}
            sdrNote={currentContact?.notesSDR ?? null}
            isSDR={false}
            isTransferred={isOnTransfer}
            activeVoicemail={isOnVoicemail}
            onSaveAgentNote={handleAgentNotesFromCard}
            onSaveSdrNote={undefined}
          />
        </DialerBodyGate>

      </DialerLayout>

      <div className="w-full shrink-0">
        <Card className={clsx(
          'w-full bg-primary/5 rounded-2xl shadow-md border border-border px-3 md:px-4 py-1',
          isOnTransfer && 'border-(--ring)',
          isOnVoicemail && 'border-(--purple)',
        )}>
          <CardContent className="flex flex-col md:flex-row justify-between items-start md:items-center gap-1.5 md:gap-2 lg:gap-3 px-0 py-1 text-xs md:text-sm lg:text-base">
            <div className="flex items-center gap-1.5 md:gap-2">
              <User className="size-3.5 md:size-4 lg:size-5 text-primary" />
              <span><span className="font-medium">{t('agentLayout.sdr', 'User')}:</span> {userRaw?.sdrName}</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <Building2 className="size-3.5 md:size-4 lg:size-5 text-primary" />
              <span><span className="font-medium">{t('agentLayout.organization', 'Organization')}:</span> {userRaw?.organizationName}</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <LocateFixed className="size-3.5 md:size-4 lg:size-5 text-primary" />
              <span><span className="font-medium">{t('agentLayout.session', 'Session')}:</span> {callSessionId ?? '—'}</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <ClipboardList className="size-3.5 md:size-4 lg:size-5 text-primary" />
              <span><span className="font-medium">{t('agentLayout.queue', 'Remaining contacts')}:</span> {queueLeft ?? 0}</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <Key className="size-3.5 md:size-4 lg:size-5 text-primary" />
              <span><span className="font-medium">Twilio:</span> {twilioStatus}</span>
            </div>
          </CardContent>
        </Card>

        <DispositionsModal
          id={dispositionTarget?.contactId ?? ''}
          name={dispositionTarget?.contactName ?? '—'}
          open={showDisposition}
          onOpenChange={setShowDisposition}
          options={DISPOSITION_OPTIONS}
          value={dispositionValue}
          onChange={onDispositionChange}
          agentNotes={agentNotes !== '' ? agentNotes : ''}
          onNotesChange={setAgentNotes}
          showAgentNotes={true}
          showSpecify={showSpecify}
          submitDisabled={submitDisabled}
          fuegoLead={fuegoLead}
          onFuegoLeadChange={setFuegoLead}
          onSubmit={handleDispositionSubmitVoid}
          showFuegoLead={true}
        />
      </div>
    </div>
  );
}
